/**
 * ProductBadge — text chip painted on top of a product image.
 *
 * Rendered as an absolutely-positioned element inside a `position: relative`
 * container. When `fontSize` is 0 (default) the badge auto-sizes from the
 * container's rendered width so it stays legible on both the 40px admin
 * thumbnail and the 400px storefront card. All other visual knobs
 * (location, background, foreground, font family, rotation) are pass-through
 * config from the product record.
 */
import { useEffect, useRef, useState } from "react";

const POSITION_STYLE = {
  "top-left":     { top: "8px",   left: "8px",   transformOrigin: "left top" },
  "top-right":    { top: "8px",   right: "8px",  transformOrigin: "right top" },
  "bottom-left":  { bottom: "8px", left: "8px",  transformOrigin: "left bottom" },
  "bottom-right": { bottom: "8px", right: "8px", transformOrigin: "right bottom" },
  "center":       { top: "50%",   left: "50%",   transformOrigin: "center center", translate: "-50% -50%" },
};

export function ProductBadge({
  text,
  position = "top-right",
  bgColor = "#ef4444",
  textColor = "#ffffff",
  fontFamily = "",
  fontSize = 0,
  rotation = 0,
  testId,
}) {
  const wrapRef = useRef(null);
  const [autoSize, setAutoSize] = useState(14);

  // Auto-size the badge from the parent container's width so the same
  // config looks proportionate on a 40px admin thumb and a 400px product
  // card. Kicks in only when the admin leaves `fontSize` at 0 / unset.
  useEffect(() => {
    if (fontSize && Number(fontSize) > 0) return undefined;
    const el = wrapRef.current?.parentElement;
    if (!el) return undefined;
    const compute = () => {
      const w = el.getBoundingClientRect().width;
      // ~6% of container width, clamped to a legible range on any card.
      setAutoSize(Math.max(9, Math.min(48, Math.round(w * 0.06))));
    };
    compute();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(compute);
      ro.observe(el);
    }
    window.addEventListener("resize", compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [fontSize]);

  if (!text) return null;

  const pos = POSITION_STYLE[position] || POSITION_STYLE["top-right"];
  const px = Number(fontSize) > 0 ? Number(fontSize) : autoSize;
  const baseTranslate = pos.translate ? `translate(${pos.translate}) ` : "";
  const rotate = Number(rotation) || 0;

  return (
    <span
      ref={wrapRef}
      className="pointer-events-none absolute z-10 rounded px-2 py-0.5 font-semibold uppercase tracking-wide shadow-md"
      style={{
        top: pos.top,
        left: pos.left,
        right: pos.right,
        bottom: pos.bottom,
        transformOrigin: pos.transformOrigin,
        transform: `${baseTranslate}rotate(${rotate}deg)`,
        backgroundColor: bgColor,
        color: textColor,
        fontFamily: fontFamily || "inherit",
        fontSize: `${px}px`,
        lineHeight: 1.15,
        whiteSpace: "nowrap",
      }}
      data-testid={testId}
    >
      {text}
    </span>
  );
}

// Named preview alias — used by the admin dialog. Same visual contract as
// the storefront badge; kept as a distinct export so grep-ing for either
// usage is unambiguous.
export function ProductBadgePreview({
  text, position, bgColor, textColor, fontFamily, fontSize, rotation,
}) {
  return (
    <ProductBadge
      text={text}
      position={position}
      bgColor={bgColor}
      textColor={textColor}
      fontFamily={fontFamily}
      fontSize={fontSize}
      rotation={rotation}
      testId="product-badge-preview"
    />
  );
}

export default ProductBadge;
